/**
 * Phone display formatting for RTL screens.
 *
 * Israeli numbers render in LOCAL format with dashes (`053-352-3159`), foreign ones stay
 * international (`+66630606931`). Every result is wrapped in Unicode LTR-ISOLATE marks
 * (U+2066…U+2069) so it reads left-to-right even inside Hebrew text — without them the bidi
 * algorithm scrambles the `+`/dash segments (`+972…` famously renders as `972…+`). The isolates
 * are invisible and survive ellipsis/`noWrap`, so callers need no `direction: ltr` styling.
 */

const LRI = "⁦"; // LEFT-TO-RIGHT ISOLATE
const PDI = "⁩"; // POP DIRECTIONAL ISOLATE

const isolate = (text: string): string => `${LRI}${text}${PDI}`;

const dashIsraeliLocal = (local: string): string => {
  if (local.length === 10) return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`; // 05X-XXX-XXXX
  if (local.length === 9) return `${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`; // 0X-XXX-XXXX
  return local;
};

/** Accepts any form — waId digits, E.164, Meta's spaced display string. */
export const formatPhoneLocal = (raw?: string): string => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  if (digits.startsWith("972")) {
    return isolate(dashIsraeliLocal(`0${digits.slice(3)}`));
  }
  if (digits.startsWith("0") && (digits.length === 9 || digits.length === 10)) {
    return isolate(dashIsraeliLocal(digits));
  }
  // Foreign number — no meaningful "local" form; keep international, LTR-isolated.
  return isolate(`+${digits}`);
};
